'use client'

import { Image as ImageIcon } from 'lucide-react'
import { getServerSideURL } from '@/utilities/getURL'
import { RichText } from '@/components/RichText'

type UploadedLike = {
  url?: string | null
  filename?: string | null
  alt?: string | null
  focalX?: number | null
  focalY?: number | null
}

type ImageTextBlockProps = {
  blockType: 'pageImageText'
  id?: string | null
  image?: UploadedLike | string | null
  content?: any
  imagePosition?: 'left' | 'right' | null
}

export const ImageTextBlockComponent: React.FC<ImageTextBlockProps> = ({
  image,
  content,
  imagePosition = 'left',
}) => {
  const media = typeof image === 'object' ? image : null
  const serverURL = getServerSideURL()
  const imageURL = media?.url
    ? `${serverURL}${media.url}`
    : media?.filename
      ? `${serverURL}/media/${media.filename}`
      : null
  const focalX = typeof media?.focalX === 'number' ? media.focalX : 50
  const focalY = typeof media?.focalY === 'number' ? media.focalY : 50

  const isImageRight = imagePosition === 'right'

  return (
    <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-2 md:gap-16">
      {/* Image - always first on mobile, position controlled on desktop */}
      <div
        className={`relative flex justify-center ${
          isImageRight ? 'md:order-2' : 'md:order-1'
        }`}
      >
        {/* Decorative background circle */}
        <div className="absolute inset-0 m-auto aspect-square w-full rounded-full bg-primary/5" />

        {/* Image container */}
        <figure className="group relative aspect-square w-[90%] overflow-hidden rounded-full ring-2 ring-border/10 transition-all duration-500 hover:ring-primary/20">
          {imageURL ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              alt={media?.alt || 'Afbeelding'}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              src={imageURL}
              style={{ objectPosition: `${focalX}% ${focalY}%` }}
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center bg-muted/10">
              <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
            </div>
          )}
        </figure>
      </div>

      {/* Text content */}
      <div className={`${isImageRight ? 'md:order-1' : 'md:order-2'}`}>
        {content && (
          <article className="prose-content">
            <RichText data={content} enableGutter={false} enableProse={false} />
          </article>
        )}
      </div>
    </div>
  )
}
